import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import { VideoTestData } from '../types';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowUpDown, ArrowUp, ArrowDown, Trophy, MoreVertical, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MetricColumn } from '@/components/metrics/shared/MetricsColumnFilter';
import { PitchRetentionCell } from '@/components/metrics/pitch/PitchRetentionCell';
import { TrafficEditModal } from '../TrafficEditModal';

interface ABTestPerformanceTableProps {
  videos: VideoTestData[];
  isEditable: boolean;
  onTrafficUpdate?: (videoId: string, percentage: number) => void;
  columns?: MetricColumn[];
  /** Refaz o fetch das métricas após o usuário configurar o pitch. */
  onPitchSaved?: () => void;
  /** Posição do pitch (0 = 1º) que preenche a coluna "Retenção no Pitch". */
  pitchPosition?: number;
  /** Mostra o nome do pitch de cada vídeo na célula (seletor com 2+ opções). */
  showPitchName?: boolean;
}

type SortField = 'name' | 'views' | 'plays' | 'playsUnique' | 'playRate' | 'avgEngagement' | 'pitchRetention' | 'buttonClicks';

export function ABTestPerformanceTable({
  videos,
  isEditable,
  onTrafficUpdate,
  columns,
  onPitchSaved,
  pitchPosition = 0,
  showPitchName = false,
}: ABTestPerformanceTableProps) {
  useLanguage();
  const [trafficModalOpen, setTrafficModalOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('views');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedVideos = useMemo(() => {
    // Sem pitch na posição ordena como -1: fica no fim da ordem decrescente.
    const sortValue = (video: VideoTestData) =>
      sortField === 'pitchRetention'
        ? video.pitches?.[pitchPosition]?.retentionPct ?? -1
        : (video as any)[sortField] ?? 0;
    return [...videos].sort((a, b) => {
      const aValue = sortValue(a);
      const bValue = sortValue(b);
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    });
  }, [videos, sortField, sortDirection, pitchPosition]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 text-[#AEB8C9]" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-[#4B7BFF]" />
      : <ArrowDown className="w-3.5 h-3.5 text-[#4B7BFF]" />;
  };

  const isColumnVisible = (columnId: string) => {
    if (!columns) return true;
    const column = columns.find(c => c.id === columnId);
    return column ? column.visible : true;
  };

  const formatNumber = (num: number) => {
    const safeNum = Number(num) || 0;
    return safeNum >= 1000 ? (safeNum / 1000).toFixed(1) + 'k' : safeNum.toLocaleString(getInterfaceLanguage());
  };

  const safeToFixed = (value: any, decimals = 1): string => {
    const num = Number(value);
    return isNaN(num) ? '0.0' : num.toFixed(decimals);
  };

  const handleSaveTraffic = (distribution: Record<string, number>) => {
    Object.entries(distribution).forEach(([videoId, percentage]) => {
      onTrafficUpdate?.(videoId, percentage);
    });
    setTrafficModalOpen(false);
  };

  const totals = videos.reduce(
    (acc, v) => ({
      views: acc.views + v.views,
      plays: acc.plays + v.plays,
      playsUnique: acc.playsUnique + v.playsUnique,
      buttonClicks: acc.buttonClicks + v.buttonClicks,
    }),
    { views: 0, plays: 0, playsUnique: 0, buttonClicks: 0 }
  );

  const avgPlayRate = videos.length > 0
    ? parseFloat((videos.reduce((sum, v) => sum + (v.playRate || 0), 0) / videos.length).toFixed(2))
    : 0;
  const avgEngagement = videos.length > 0
    ? parseFloat((videos.reduce((sum, v) => sum + (v.avgEngagement || 0), 0) / videos.length).toFixed(2))
    : 0;

  const headerClass = "bg-[#F7F9FF] text-[13px] font-semibold text-[#5A6475] cursor-pointer group hover:bg-[#EDF1FF] transition-colors whitespace-nowrap";
  const headerStyle = { borderBottom: '1px solid #EDF1FF', padding: '14px 18px', height: '56px' };

  return (
    <>
      <div className="overflow-hidden rounded-[14px]" style={{ background: '#FFFFFF', border: '1px solid #EDF1FF', boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.04)' }}>
        {/* Desktop Table */}
        <div className="overflow-x-auto hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow className="border-0">
                <TableHead className={cn(headerClass, "min-w-[280px]")} style={headerStyle} onClick={() => handleSort('name')}>
                  <div className="flex items-center gap-2">{translateInterfaceText("Vídeo")}{" "}{getSortIcon('name')}</div>
                </TableHead>
                {isColumnVisible('views') && (
                  <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('views')}>
                    <div className="flex items-center gap-2">{translateInterfaceText("Visualizações")}{" "}{getSortIcon('views')}</div>
                  </TableHead>
                )}
                {isColumnVisible('plays') && (
                  <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('plays')}>
                    <div className="flex items-center gap-2">{translateInterfaceText("Plays")}{" "}{getSortIcon('plays')}</div>
                  </TableHead>
                )}
                {isColumnVisible('playsUnique') && (
                  <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('playsUnique')}>
                    <div className="flex items-center gap-2">{translateInterfaceText("Plays Únicos")}{" "}{getSortIcon('playsUnique')}</div>
                  </TableHead>
                )}
                {isColumnVisible('playRate') && (
                  <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('playRate')}>
                    <div className="flex items-center gap-2">{translateInterfaceText("Taxa de Play")}{" "}{getSortIcon('playRate')}</div>
                  </TableHead>
                )}
                {isColumnVisible('engagement') && (
                  <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('avgEngagement')}>
                    <div className="flex items-center gap-2">{translateInterfaceText("Engajamento")}{" "}{getSortIcon('avgEngagement')}</div>
                  </TableHead>
                )}
                {isColumnVisible('pitchRetention') && (
                  <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('pitchRetention')}>
                    <div className="flex items-center gap-2">{translateInterfaceText("Retenção no Pitch")}{" "}{getSortIcon('pitchRetention')}</div>
                  </TableHead>
                )}
                {isColumnVisible('buttonClicks') && (
                  <TableHead className={headerClass} style={headerStyle} onClick={() => handleSort('buttonClicks')}>
                    <div className="flex items-center gap-2">{translateInterfaceText("Cliques")}{" "}{getSortIcon('buttonClicks')}</div>
                  </TableHead>
                )}
                {isEditable && (
                  <TableHead className={headerClass} style={headerStyle}>
                    {translateInterfaceText("Ações")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedVideos.map((video, index) => (
                <TableRow
                  key={video.id}
                  className={cn(
                    index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFF]',
                    'hover:bg-[#F3F6FF] transition-all duration-150 border-b border-[#EDF1FF]'
                  )}
                  style={{ height: '56px' }}
                >
                  <TableCell className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-9 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        <img src={video.thumbnail} alt={video.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-semibold text-[#2C3345] truncate max-w-[180px]">{video.name}</span>
                        {video.isWinning && (
                          <Badge className="text-[12px] font-medium px-2.5 py-1 bg-primary text-white border-primary rounded-full flex items-center gap-1">
                            <Trophy className="w-3 h-3" />
                            {translateInterfaceText("Vencendo")}</Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  {isColumnVisible('views') && (
                    <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.views)}</TableCell>
                  )}
                  {isColumnVisible('plays') && (
                    <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.plays)}</TableCell>
                  )}
                  {isColumnVisible('playsUnique') && (
                    <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.playsUnique)}</TableCell>
                  )}
                  {isColumnVisible('playRate') && (
                    <TableCell className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-medium" style={{ color: '#5A6475' }}>
                        {safeToFixed(video.playRate)}%
                      </span>
                    </TableCell>
                  )}
                  {isColumnVisible('engagement') && (
                    <TableCell className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-medium" style={{ color: '#5A6475' }}>
                        {safeToFixed(video.avgEngagement)}%
                      </span>
                    </TableCell>
                  )}
                  {isColumnVisible('pitchRetention') && (
                    <TableCell className="py-3 px-4">
                      <PitchRetentionCell
                        pitch={video.pitches?.[pitchPosition] ?? null}
                        videoPitches={video.pitches ?? []}
                        pitchVideoId={video.pitchVideoId}
                        durationInSeconds={video.videoDuration}
                        showName={showPitchName}
                        onSaved={onPitchSaved}
                      />
                    </TableCell>
                  )}
                  {isColumnVisible('buttonClicks') && (
                    <TableCell className="py-3 px-4 text-[14px] text-[#2C3345]">{formatNumber(video.buttonClicks)}</TableCell>
                  )}
                  {isEditable && (
                    <TableCell className="py-3 px-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:-translate-y-[1px] transition-transform">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setTrafficModalOpen(true)}>
                            <Settings className="h-4 w-4 mr-2" />
                            {translateInterfaceText("Editar tráfego")}</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}

              {/* Totals Row */}
              <TableRow className="bg-[#F7F9FF] border-t-2 border-[#EDF1FF] font-semibold" style={{ height: '56px' }}>
                <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{translateInterfaceText("Total")}</TableCell>
                {isColumnVisible('views') && (
                  <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.views)}</TableCell>
                )}
                {isColumnVisible('plays') && (
                  <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.plays)}</TableCell>
                )}
                {isColumnVisible('playsUnique') && (
                  <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.playsUnique)}</TableCell>
                )}
                {isColumnVisible('playRate') && (
                  <TableCell className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-bold" style={{ color: '#5A6475' }}>
                      {safeToFixed(avgPlayRate)}%
                    </span>
                  </TableCell>
                )}
                {isColumnVisible('engagement') && (
                  <TableCell className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[13px] font-bold" style={{ color: '#5A6475' }}>
                      {safeToFixed(avgEngagement)}%
                    </span>
                  </TableCell>
                )}
                {/* Sem total de pitch: média de percentuais entre vídeos não
                    tem significado (cada um tem base de plays diferente). */}
                {isColumnVisible('pitchRetention') && <TableCell className="py-3 px-4" />}
                {isColumnVisible('buttonClicks') && (
                  <TableCell className="py-3 px-4 text-[14px] text-[#2C3345] font-bold">{formatNumber(totals.buttonClicks)}</TableCell>
                )}
                {isEditable && <TableCell />}
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4 p-4">
          {videos.map((video) => (
            <Card key={video.id} className="p-4">
              <div className="flex items-start gap-3 mb-4">
                <img src={video.thumbnail} alt={video.name} className="w-24 h-16 object-cover rounded flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground truncate mb-1">{video.name}</h3>
                  {video.isWinning && (
                    <Badge className="text-[12px] font-medium px-2.5 py-1 bg-primary text-white border-primary rounded-full flex items-center gap-1 w-fit">
                      <Trophy className="w-3 h-3" />
                      {translateInterfaceText("Vencendo")}</Badge>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-muted-foreground">{translateInterfaceText("Visualizações")}</div><div className="font-medium">{formatNumber(video.views)}</div></div>
                <div><div className="text-muted-foreground">{translateInterfaceText("Plays")}</div><div className="font-medium">{formatNumber(video.plays)}</div></div>
                <div><div className="text-muted-foreground">{translateInterfaceText("Taxa de Play")}</div><div className="font-medium">{safeToFixed(video.playRate)}%</div></div>
                <div><div className="text-muted-foreground">{translateInterfaceText("Engajamento")}</div><div className="font-medium">{safeToFixed(video.avgEngagement)}%</div></div>
                <div><div className="text-muted-foreground">{translateInterfaceText("Cliques")}</div><div className="font-medium">{formatNumber(video.buttonClicks)}</div></div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {isEditable && (
        <TrafficEditModal
          open={trafficModalOpen}
          onOpenChange={setTrafficModalOpen}
          allVideos={videos}
          onSave={handleSaveTraffic}
        />
      )}
    </>
  );
}
